/**
 * Recent Flagged List Component
 *
 * Displays a list of recently flagged phone numbers.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn, formatDate, formatPhoneNumber } from '@/utils';
import { Logger, LogCategory } from '@/utils/logger';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { FlagStatusBadge, ReputationSourcesList } from '@/components/ui/Badge';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { ROUTES } from '@/constants';
import type { RecentFlaggedPhone, ReputationSource } from '@/types';
import { ChevronRight, AlertTriangle } from 'lucide-react';

// ============================================================================
// Component
// ============================================================================

export function RecentFlaggedList() {
  const navigate = useNavigate();
  const [data, setData] = useState<RecentFlaggedPhone[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const company = useAuthStore((state) => state.company);

  useEffect(() => {
    if (!company?.id) {
      setIsLoading(false);
      return;
    }

    async function fetchRecentFlagged() {
      try {
        Logger.info(LogCategory.DATA, 'Loading recent flagged phones', 'RecentFlaggedList');

        const { data: phones, error } = await supabase
          .from('phone_numbers')
          .select('id, number, formatted_number, last_flagged_date, reputation_sources')
          .eq('company_id', company!.id)
          .eq('flag_status', 'flagged')
          .eq('is_archived', false)
          .order('last_flagged_date', { ascending: false })
          .limit(10);

        if (error) throw error;

        const recentFlagged: RecentFlaggedPhone[] = (phones || []).map((p: any) => ({
          id: p.id,
          number: p.number,
          formattedNumber: p.formatted_number,
          flagType: p.reputation_sources?.length > 0 ? `Flagged by ${p.reputation_sources.length} source${p.reputation_sources.length > 1 ? 's' : ''}` : 'Flagged',
          lastFlaggedDate: p.last_flagged_date || new Date().toISOString(),
          sources: (p.reputation_sources || []) as ReputationSource[],
        }));

        setData(recentFlagged);
      } catch (err) {
        Logger.error(LogCategory.DATA, 'Failed to load recent flagged phones', 'RecentFlaggedList', undefined, err);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    }

    fetchRecentFlagged();
  }, [company?.id]);

  const handleViewDetails = (phoneId: string) => {
    Logger.info(LogCategory.NAVIGATION, 'Navigating to phone details', 'RecentFlaggedList', { phoneId });
    navigate(`${ROUTES.PHONE_NUMBERS}/${phoneId}`);
  };

  const handleViewAll = () => {
    Logger.info(LogCategory.NAVIGATION, 'Navigating to all phone numbers', 'RecentFlaggedList');
    navigate(`${ROUTES.PHONE_NUMBERS}?status=flagged`);
  };

  return (
    <Card>
      <CardHeader
        title="Recently Flagged"
        description="Phone numbers recently flagged by reputation sources"
        action={
          <Button variant="ghost" size="sm" onClick={handleViewAll} rightIcon={<ChevronRight className="h-4 w-4" />}>
            View all
          </Button>
        }
      />
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="h-10 w-10 bg-slate-200 rounded-full" />
                <div className="flex-1">
                  <div className="h-4 bg-slate-200 rounded w-32 mb-2" />
                  <div className="h-3 bg-slate-200 rounded w-48" />
                </div>
              </div>
            ))}
          </div>
        ) : data.length === 0 ? (
          <div className="py-8 text-center">
            <div className="mx-auto h-12 w-12 text-slate-300 mb-4">
              <AlertTriangle className="h-full w-full" />
            </div>
            <h3 className="text-sm font-medium text-slate-900">No flagged numbers</h3>
            <p className="mt-1 text-sm text-slate-500">
              All your phone numbers are clean!
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.map((phone) => (
              <div
                key={phone.id}
                onClick={() => handleViewDetails(phone.id)}
                className={cn(
                  'flex items-center gap-4 p-3 -mx-3 rounded-lg cursor-pointer',
                  'hover:bg-slate-50 transition-colors'
                )}
              >
                {/* Flag indicator */}
                <div className="h-10 w-10 bg-danger-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-danger-600" />
                </div>

                {/* Phone info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-medium text-slate-900">
                      {phone.formattedNumber}
                    </span>
                    <FlagStatusBadge status="flagged" size="sm" />
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-sm text-slate-500">
                      {phone.flagType}
                    </span>
                    <span className="text-slate-300">•</span>
                    <span className="text-sm text-slate-500">
                      {formatDate(phone.lastFlaggedDate, 'relative')}
                    </span>
                  </div>
                </div>

                {/* Sources */}
                <div className="hidden sm:block">
                  <ReputationSourcesList sources={phone.sources} maxVisible={2} />
                </div>

                {/* Arrow */}
                <ChevronRight className="h-5 w-5 text-slate-400" />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
